import React, { useMemo } from 'react';
import { GetDayOnPressHandler, MonthYear, RenderCalendarContent, RenderDayContentsArgs } from './types';
import { CalendarMonthHelper } from './CalendarMonthHelper';
import Stack from '../Stack';
import Text from '../Text';
import Day from './Day';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import Divider from '../Divider';
import { CALENDAR_PLACEHOLDER_HEIGHT } from './constants';
import { StyleProp, ViewStyle } from 'react-native';
import { DateTime } from 'luxon';
import DateMonthScanHeader from './DateScanHeader/DateMonthScanHeader';
import YearScanMonthSelector from './YearScanMonthSelector';
import YearScanHeader from './DateScanHeader/YearScanHeader';

export type CalendarOwnProps = {
    monthYear: MonthYear;
    onMonthYearChange: (monthYear: MonthYear) => void;
    renderDayContents?: (args: RenderDayContentsArgs) => React.ReactNode;
    getOnDayPressHandler?: GetDayOnPressHandler;
    renderCalendarContents?: (monthYear: MonthYear) => React.ReactNode;
    renderCalendarContent?: RenderCalendarContent;
    renderHeader?: () => React.ReactNode;
    renderYearScanMonth?: (monthYear: MonthYear) => React.ReactNode;
    style?: StyleProp<ViewStyle>;
};

export type CalendarProps = CalendarOwnProps;

const Calendar: React.FC<CalendarProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const calendarMonthHelper = useMemo(() => CalendarMonthHelper.getFromMonthYear(props.monthYear), [props.monthYear]);
    const [yearScanValue, setYearScanValue] = React.useState<number | null>(null);

    const renderCalendarContent = () => {
        if (yearScanValue !== null) {
            return (
                <YearScanMonthSelector
                    yearScanValue={yearScanValue}
                    onMonthYearChange={(monthYear) => {
                        props.onMonthYearChange(monthYear);
                        setYearScanValue(null);
                    }}
                    height={CALENDAR_PLACEHOLDER_HEIGHT}
                    renderYearScanMonth={props.renderYearScanMonth}
                />
            );
        } else if (props.renderCalendarContent) {
            return props.renderCalendarContent({
                monthYear: props.monthYear,
                placeholderHeight: CALENDAR_PLACEHOLDER_HEIGHT,
            });
        } else {
            return (
                <Stack vertical style={{ alignSelf: 'stretch' }}>
                    {calendarMonthHelper.getWeeks()?.[0] ? (
                        <Stack horizontal style={{ alignSelf: 'stretch', marginBottom: 8 }}>
                            {calendarMonthHelper.getWeeks()[0].map((calendarDay, index) => (
                                <Text
                                    key={index}
                                    variant="caption-1"
                                    color="Gray-Text"
                                    style={{ flex: 1, textAlign: 'center' }}
                                >
                                    {DateTime.fromISO(calendarDay.isoDate).toFormat('ccc')}
                                </Text>
                            ))}
                        </Stack>
                    ) : null}
                    {calendarMonthHelper.getWeeks().map((week, index) => (
                        <Stack key={index} horizontal style={{ alignSelf: 'stretch', marginBottom: 4 }}>
                            {week.map((calendarDay, index) => (
                                <Day
                                    key={index}
                                    calendarDay={calendarDay}
                                    getOnDayPressHandler={props.getOnDayPressHandler}
                                    renderDayContents={props.renderDayContents}
                                />
                            ))}
                        </Stack>
                    ))}
                </Stack>
            );
        }
    };

    return (
        <Stack vertical style={props.style}>
            {props.renderHeader ? props.renderHeader() : null}
            {yearScanValue !== null ? (
                <YearScanHeader yearScanValue={yearScanValue} setYearScanValue={setYearScanValue} />
            ) : (
                <DateMonthScanHeader
                    previousMonthYear={calendarMonthHelper.previousMonthYear}
                    nextMonthYear={calendarMonthHelper.nextMonthYear}
                    monthYearDisplay={calendarMonthHelper.monthYearDisplay}
                    onSwitchToYearScan={() => setYearScanValue(calendarMonthHelper.currentYear)}
                    onMonthYearChange={props.onMonthYearChange}
                />
            )}
            <Divider horizontal style={{ marginBottom: 8, backgroundColor: isMobile ? undefined : 'transparent' }} />
            {renderCalendarContent()}
        </Stack>
    );
};

export default React.memo(Calendar);
